/**
 * Quick Search Module
 * Enterprise-level search autocomplete with fuzzy matching, recent/popular searches,
 * keyboard navigation, caching, and click tracking
 */
export class QuickSearch {
    constructor() {
        this.debounceTimer = null;
        this.minChars = 2;
        this.debounceDelay = 200;
        this.isOpen = false;
        this.activeIndex = -1;
        this.currentSearchQueryId = null;
        this.cache = new Map();
        this.cacheTTL = 60000; // 1 minute cache
        this.isMobile = window.innerWidth <= 768;
        this.searchInput = document.querySelector('[data-quick-search]');
        this.resultsContainer = document.querySelector('[data-quick-search-results]');
        if (this.searchInput) {
            this.init();
        }
        // Listen for resize to detect mobile
        window.addEventListener('resize', () => {
            this.isMobile = window.innerWidth <= 768;
        });
    }
    init() {
        if (!this.searchInput)
            return;
        // Create results container if not exists
        if (!this.resultsContainer) {
            this.resultsContainer = document.createElement('div');
            this.resultsContainer.className = 'quick-search-results';
            this.resultsContainer.setAttribute('data-quick-search-results', '');
            this.resultsContainer.setAttribute('role', 'listbox');
            this.resultsContainer.setAttribute('aria-label', 'Search suggestions');
            this.searchInput.parentElement?.appendChild(this.resultsContainer);
        }
        // Set ARIA attributes on input
        this.searchInput.setAttribute('role', 'combobox');
        this.searchInput.setAttribute('aria-autocomplete', 'list');
        this.searchInput.setAttribute('aria-expanded', 'false');
        this.searchInput.setAttribute('aria-controls', 'quick-search-results');
        this.resultsContainer.id = 'quick-search-results';
        // Bind events
        this.searchInput.addEventListener('input', this.handleInput.bind(this));
        this.searchInput.addEventListener('focus', this.handleFocus.bind(this));
        this.searchInput.addEventListener('keydown', this.handleKeydown.bind(this));
        // Close on click outside
        document.addEventListener('click', (e) => {
            if (!this.searchInput?.contains(e.target) &&
                !this.resultsContainer?.contains(e.target)) {
                this.close();
            }
        });
        // Close on escape key (global)
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.close();
                this.searchInput?.focus();
            }
        });
    }
    handleInput(e) {
        const query = e.target.value.trim();
        this.activeIndex = -1;
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
        // Show recent/popular immediately on empty input
        if (query.length === 0) {
            this.fetchRecentAndPopular();
            return;
        }
        if (query.length < this.minChars) {
            this.close();
            return;
        }
        this.debounceTimer = setTimeout(() => {
            this.search(query);
        }, this.debounceDelay);
    }
    handleFocus() {
        const query = this.searchInput?.value.trim() || '';
        // Show recent/popular on focus with empty input
        if (query.length === 0) {
            this.fetchRecentAndPopular();
        }
        else if (query.length >= this.minChars && this.resultsContainer?.innerHTML) {
            this.open();
        }
    }
    handleKeydown(e) {
        if (!this.isOpen) {
            // Allow enter to submit form when closed
            if (e.key === 'Enter') {
                return;
            }
            return;
        }
        switch (e.key) {
            case 'Escape':
                e.preventDefault();
                this.close();
                break;
            case 'ArrowDown':
                e.preventDefault();
                this.navigateResults(1);
                break;
            case 'ArrowUp':
                e.preventDefault();
                this.navigateResults(-1);
                break;
            case 'Enter':
                const active = this.resultsContainer?.querySelector('.is-active');
                if (active) {
                    e.preventDefault();
                    const link = active.closest('a') || active.querySelector('a');
                    if (link) {
                        this.trackAndNavigate(link);
                    }
                }
                break;
            case 'Tab':
                // Close on tab but allow normal tab behavior
                this.close();
                break;
        }
    }
    navigateResults(direction) {
        if (!this.resultsContainer)
            return;
        const items = this.resultsContainer.querySelectorAll('.quick-search-item');
        if (items.length === 0)
            return;
        // Remove current active
        const currentActive = this.resultsContainer.querySelector('.is-active');
        currentActive?.classList.remove('is-active');
        // Calculate new index
        this.activeIndex += direction;
        if (this.activeIndex < 0) {
            this.activeIndex = items.length - 1;
        }
        else if (this.activeIndex >= items.length) {
            this.activeIndex = 0;
        }
        // Set new active
        const newActive = items[this.activeIndex];
        newActive?.classList.add('is-active');
        newActive?.scrollIntoView({ block: 'nearest' });
        // Update ARIA
        this.searchInput?.setAttribute('aria-activedescendant', newActive?.id || '');
    }
    async fetchRecentAndPopular() {
        try {
            this.showLoading();
            const response = await fetch('/search/quick?q=');
            const data = await response.json();
            this.renderRecentAndPopular(data);
            this.open();
        }
        catch (error) {
            console.error('Error fetching recent/popular:', error);
            this.close();
        }
    }
    async search(query) {
        // Check cache first
        const cached = this.getFromCache(query);
        if (cached) {
            this.renderResults(cached, query);
            this.open();
            return;
        }
        try {
            this.showLoading();
            const response = await fetch(`/search/quick?q=${encodeURIComponent(query)}`);
            const data = await response.json();
            // Cache the result
            this.setCache(query, data);
            this.renderResults(data, query);
            this.open();
        }
        catch (error) {
            console.error('Search error:', error);
            this.close();
        }
    }
    getFromCache(query) {
        const entry = this.cache.get(query.toLowerCase());
        if (!entry)
            return null;
        // Check if expired
        if (Date.now() - entry.timestamp > this.cacheTTL) {
            this.cache.delete(query.toLowerCase());
            return null;
        }
        return entry.data;
    }
    setCache(query, data) {
        // Limit cache size
        if (this.cache.size > 50) {
            const firstKey = this.cache.keys().next().value;
            if (firstKey)
                this.cache.delete(firstKey);
        }
        this.cache.set(query.toLowerCase(), {
            data,
            timestamp: Date.now(),
        });
    }
    renderRecentAndPopular(data) {
        if (!this.resultsContainer)
            return;
        const hasRecent = data.recentSearches && data.recentSearches.length > 0;
        const hasPopular = data.popularSearches && data.popularSearches.length > 0;
        if (!hasRecent && !hasPopular) {
            this.resultsContainer.innerHTML = '';
            this.close();
            return;
        }
        let html = '';
        let itemIndex = 0;
        // Recent searches
        if (hasRecent) {
            html += `
        <div class="quick-search-section quick-search-section--recent">
          <div class="quick-search-section__header">
            <h4 class="quick-search-section__title">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                <path d="M13 3a9 9 0 00-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0013 21a9 9 0 000-18zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/>
              </svg>
              Recent Searches
            </h4>
            <button class="quick-search-clear" data-clear-recent aria-label="Clear recent searches">
              Clear
            </button>
          </div>
          ${data.recentSearches.map(query => {
                const itemId = `search-item-${itemIndex++}`;
                return `
              <a href="/search?q=${encodeURIComponent(query)}"
                 class="quick-search-item quick-search-item--suggestion"
                 id="${itemId}"
                 role="option"
                 data-query="${this.escapeHtml(query)}">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
                <span class="quick-search-item__text">${this.escapeHtml(query)}</span>
              </a>
            `;
            }).join('')}
        </div>
      `;
        }
        // Popular searches
        if (hasPopular) {
            html += `
        <div class="quick-search-section quick-search-section--popular">
          <h4 class="quick-search-section__title">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>
            </svg>
            Trending Searches
          </h4>
          ${data.popularSearches.map(query => {
                const itemId = `search-item-${itemIndex++}`;
                return `
              <a href="/search?q=${encodeURIComponent(query)}"
                 class="quick-search-item quick-search-item--suggestion"
                 id="${itemId}"
                 role="option"
                 data-query="${this.escapeHtml(query)}">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
                <span class="quick-search-item__text">${this.escapeHtml(query)}</span>
              </a>
            `;
            }).join('')}
        </div>
      `;
        }
        this.resultsContainer.innerHTML = html;
        // Bind clear button
        const clearBtn = this.resultsContainer.querySelector('[data-clear-recent]');
        clearBtn?.addEventListener('click', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await this.clearRecentSearches();
        });
        // Bind suggestion clicks
        const suggestions = this.resultsContainer.querySelectorAll('.quick-search-item--suggestion');
        suggestions.forEach(item => {
            item.addEventListener('click', (e) => {
                const query = item.dataset.query;
                if (query && this.searchInput) {
                    this.searchInput.value = query;
                }
            });
        });
    }
    renderResults(data, query) {
        if (!this.resultsContainer)
            return;
        const hasResults = data.products.length > 0 ||
            data.categories.length > 0 ||
            data.pages.length > 0;
        if (!hasResults) {
            this.resultsContainer.innerHTML = `
        <div class="quick-search-empty">
          <svg viewBox="0 0 24 24" width="48" height="48" fill="currentColor" opacity="0.3">
            <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
          </svg>
          <p>No results found for "<strong>${this.escapeHtml(query)}</strong>"</p>
          <span class="quick-search-empty__hint">Try checking your spelling or use different keywords</span>
        </div>
      `;
            return;
        }
        let html = '';
        let itemIndex = 0;
        // Products
        if (data.products.length > 0) {
            html += `
        <div class="quick-search-section quick-search-section--products">
          <h4 class="quick-search-section__title">Products</h4>
          ${data.products.map(product => {
                const itemId = `search-item-${itemIndex++}`;
                const priceHtml = product.salePrice
                    ? `<s class="quick-search-item__price--original">$${product.price.toFixed(2)}</s> <span class="quick-search-item__price--sale">$${product.salePrice.toFixed(2)}</span>`
                    : `<span>$${product.price.toFixed(2)}</span>`;
                return `
              <a href="${product.url}"
                 class="quick-search-item quick-search-item--product"
                 id="${itemId}"
                 role="option"
                 data-type="product"
                 data-id="${product.id}">
                <img src="${product.imageUrl}"
                     alt="${this.escapeHtml(product.name)}"
                     class="quick-search-item__image"
                     loading="lazy">
                <div class="quick-search-item__details">
                  <span class="quick-search-item__name">${product.highlightedName || this.escapeHtml(product.name)}</span>
                  <span class="quick-search-item__price">${priceHtml}</span>
                </div>
                <svg class="quick-search-item__arrow" viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                </svg>
              </a>
            `;
            }).join('')}
        </div>
      `;
        }
        // Categories
        if (data.categories.length > 0) {
            html += `
        <div class="quick-search-section quick-search-section--categories">
          <h4 class="quick-search-section__title">Categories</h4>
          ${data.categories.map(category => {
                const itemId = `search-item-${itemIndex++}`;
                return `
              <a href="${category.url}"
                 class="quick-search-item quick-search-item--category"
                 id="${itemId}"
                 role="option"
                 data-type="category"
                 data-id="${category.id}">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/>
                </svg>
                <span class="quick-search-item__name">${category.highlightedName || this.escapeHtml(category.name)}</span>
                <svg class="quick-search-item__arrow" viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                </svg>
              </a>
            `;
            }).join('')}
        </div>
      `;
        }
        // Pages
        if (data.pages.length > 0) {
            html += `
        <div class="quick-search-section quick-search-section--pages">
          <h4 class="quick-search-section__title">Pages</h4>
          ${data.pages.map(page => {
                const itemId = `search-item-${itemIndex++}`;
                return `
              <a href="${page.url}"
                 class="quick-search-item quick-search-item--page"
                 id="${itemId}"
                 role="option"
                 data-type="page"
                 data-id="${page.id}">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                </svg>
                <span class="quick-search-item__name">${page.highlightedTitle || this.escapeHtml(page.title)}</span>
                <svg class="quick-search-item__arrow" viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                </svg>
              </a>
            `;
            }).join('')}
        </div>
      `;
        }
        // View all link
        html += `
      <a href="/search?q=${encodeURIComponent(query)}" class="quick-search-view-all">
        View all results for "<strong>${this.escapeHtml(query)}</strong>"
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
          <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
        </svg>
      </a>
    `;
        this.resultsContainer.innerHTML = html;
        // Bind click tracking to result items
        const items = this.resultsContainer.querySelectorAll('.quick-search-item[data-type]');
        items.forEach(item => {
            item.addEventListener('click', (e) => {
                this.trackAndNavigate(item);
            });
        });
    }
    async trackAndNavigate(link) {
        const type = link.dataset.type;
        const id = link.dataset.id;
        // Track click asynchronously, don't wait
        if (type && id && this.currentSearchQueryId) {
            fetch('/search/track-click', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    searchQueryId: this.currentSearchQueryId,
                    clickedId: parseInt(id),
                    clickedType: type,
                }),
            }).catch(() => { });
        }
        // Navigate
        window.location.href = link.href;
    }
    async clearRecentSearches() {
        try {
            await fetch('/search/recent', { method: 'DELETE' });
            // Re-fetch to show updated list
            await this.fetchRecentAndPopular();
        }
        catch (error) {
            console.error('Error clearing recent searches:', error);
        }
    }
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
    showLoading() {
        if (this.resultsContainer) {
            this.resultsContainer.innerHTML = `
        <div class="quick-search-loading">
          <div class="quick-search-spinner"></div>
          <span>Searching...</span>
        </div>
      `;
            this.open();
        }
    }
    open() {
        if (this.resultsContainer) {
            this.resultsContainer.classList.add('is-open');
            this.searchInput?.setAttribute('aria-expanded', 'true');
            this.isOpen = true;
            // Add mobile overlay
            if (this.isMobile) {
                document.body.classList.add('quick-search-open');
            }
        }
    }
    close() {
        if (this.resultsContainer) {
            this.resultsContainer.classList.remove('is-open');
            this.searchInput?.setAttribute('aria-expanded', 'false');
            this.searchInput?.removeAttribute('aria-activedescendant');
            this.isOpen = false;
            this.activeIndex = -1;
            // Remove mobile overlay
            document.body.classList.remove('quick-search-open');
        }
    }
}
export default QuickSearch;
